import type { TFunction } from 'i18next';

import {
  GetPaeEstimateItemsDto,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys } from '../../../common/hooks/useTranslation';
import numberFormatter from '../../../common/numberFormatter';
import { hasPositionSpecialistTeams } from '../Estimates/SpecialistTeamPhaseUtils';

export const isPaePositionCorrect = (p: GetPaeEstimateItemsDto) =>
  p.position !== null && p.position !== undefined;

const isPaePositionValid = (
  p: GetPaeEstimateItemsDto,
  project?: GetProjectDto,
) =>
  p.position !== undefined &&
  p.position !== null &&
  p.countryDict !== undefined &&
  p.countryDict !== null &&
  p.purchaseFormDict !== undefined &&
  p.purchaseFormDict !== null &&
  ((project?.projectPathPhases?.length || 0) === 0 || !!p.pathPhaseDict) &&
  hasPaePositionTeam(p);

const hasPaePositionTeam = (p: GetPaeEstimateItemsDto) =>
  hasPositionSpecialistTeams(p.position?.positionSpecialistTeams)
    ? !!p.specialistTeamId
    : !!p.deliveryTeamId;

const getMissingImportFields = (
  p: GetPaeEstimateItemsDto,
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  project?: GetProjectDto,
) => {
  const missingFields: string[] = [];

  if (!p.position) {
    missingFields.push(t(tk.estimates.positionName));
  }
  if (!p.countryDict) {
    missingFields.push(t(tk.estimates.positionCountry));
  }
  if (!p.purchaseFormDict) {
    missingFields.push(t(tk.estimates.purchaseForm));
  }
  if ((project?.projectPathPhases?.length || 0) > 0 && !p.pathPhaseDict) {
    missingFields.push(t(tk.estimates.positionPhase));
  }
  if (!hasPaePositionTeam(p)) {
    missingFields.push(t(tk.estimates.deliveryTeam));
  }

  return missingFields;
};

export const validate = (
  paePositions: GetPaeEstimateItemsDto[],
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  project?: GetProjectDto,
) => {
  const errors: string[] = [];
  paePositions.forEach(p => {
    if (!isPaePositionValid(p, project)) {
      const missingFields = getMissingImportFields(p, t, tk, project);
      errors.push(
        t(tk.estimates.paePositionNotReadyToImport, {
          name: p.position?.name || '-',
          number: p.paePositionSystemNumber || '-',
          amount: numberFormatter(p.purchaseAmount, true),
          missingFields: missingFields.join(', '),
        }),
      );
    }
  });
  if (errors.length > 0) {
    return {
      valid: false,
      errors: [
        t(tk.estimates.paePositionImportErrors),
        t(tk.estimates.paePositionImportErrorsDescription),
        ...errors,
      ],
    };
  }
  return {
    valid: true,
    errors: [],
  };
};

export const setPaePositionTeam = (
  p: GetPaeEstimateItemsDto,
  teamId: string,
  teamName: string,
) => {
  if (hasPositionSpecialistTeams(p.position?.positionSpecialistTeams)) {
    p.specialistTeamId = teamId || undefined;
    p.specialistTeamName = teamName || undefined;
  } else {
    p.deliveryTeamId = teamId || undefined;
    p.deliveryTeamName = teamName || undefined;
  }
};

export const getPaePositionTeamName = (p: GetPaeEstimateItemsDto) =>
  p.specialistTeamName || p.deliveryTeamName || '';
